import styled from 'styled-components';
import React, {FC} from 'react';

import {
  fontFamiliesStrings,
  fontWeights,
  palette,
  transition,
  mediaBreakpointDown,
  mediaBreakpointOnly,
  fontSizesMixins,
  mediaBreakpointUp
} from '../../../../lib/styleVariables';
import {paletteR4Secondary} from '../../../../lib/styleVariablesR4';
import {SocialMediaLinks, SocialMediaLink} from '../../socialMedia/widget';

interface FooterLink {
  title: string;
  href: string;
  highlightedText?: string;
}

interface FooterColumnProps {
  title: string;
  links: Array<FooterLink>;
}

export interface FooterProps {
  content: FooterColumnProps[];
  socialMediaLinks: SocialMediaLink[];
  bottomLinks: FooterLink[];
  copyrightText?: string;
}

const StyledFooterDiv = styled.div`
  align-self: center;
  width: 100%;
  max-width: 1081px;
  margin: 0 auto;
  padding: 64px 32px;
  ${mediaBreakpointDown('tablet')} {
    padding: 24px 24px 24px 32px;
  }
`;

const StyledListWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  ${mediaBreakpointDown('tablet')} {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`;

const StyledListColumn = styled.div`
  flex: 1;
  margin-right: 24px;
  padding-right: 5px;
  &:last-child {
    margin-right: 0;
  }
  ${mediaBreakpointDown('tablet')} {
    margin-right: 0;
    flex-basis: 50%;
    margin-bottom: 50px;
    :nth-child(odd) {
      padding-right: 16px;
    }
    :nth-child(even) {
      padding-right: 0;
    }
  }
  ${mediaBreakpointOnly('tablet')} {
    flex-basis: 33.3333%;
  }
`;

const StyledListColumnTitle = styled.h5`
  ${fontSizesMixins.text2()};
  font-weight: ${fontWeights.bold};
  font-family: ${fontFamiliesStrings.body};
  margin-top: 0;
  margin-bottom: 24px;
`;

const StyledListColumnLinksList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const StyledListColumnLinksListItem = styled.li`
  ${fontSizesMixins.text4()}
  font-weight: ${fontWeights.normal};
  font-family: ${fontFamiliesStrings.body};
  &:not(:last-child) {
    margin-bottom: 11px;
  }
`;

const bottomLinksStyle = `
  margin-right: 16px;
`;

const StyledLink = styled.a<{isBottom?: boolean}>`
  color: currentColor;
  text-decoration: none;
  transition: color ${transition.fast};
  &:hover {
    color: ${paletteR4Secondary.blue.lowlight};
  }
  ${props => (props.isBottom ? bottomLinksStyle : '')}
`;

const StyledBottomDiv = styled.div`
  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
    align-items: flex-start;
  }
  ${mediaBreakpointUp('desktop')} {
    margin-top: 70px;
  }
`;

const bottomTextStyle = `
  font-family: ${fontFamiliesStrings.body};
  font-size: 12px;
  line-height: 162%;
  ${mediaBreakpointDown('tablet')} {
    text-align: left;
  }
`;
const StyledBottomCenterDiv = styled.div`
  font-weight: ${fontWeights.bold};
  ${mediaBreakpointDown('tablet')} {
    margin-top: 18px;
  }
  ${bottomTextStyle}
`;
const StyledBottomRightDiv = styled.div`
  font-weight: ${fontWeights.normal};
  ${mediaBreakpointDown('tablet')} {
    margin-top: 18px;
  }
  ${bottomTextStyle}
`;

const StyledBottomLinksWrapper = styled.div`
  display: flex;
  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
  }
`;

const StyledHighlightedSpan = styled.span`
  color: ${paletteR4Secondary.blue.lowlight};
  font-weight: bold;
`;

const StyledSocialMediaLinksWrapper = styled.div`
  flex: 1;
`;

const StyledLineBreak = styled.div`
  width: 100%;
  height: 1px;
  margin-top: 24px;
  margin-bottom: 20px;
  border-bottom: 2px solid ${palette.white.base};
  [data-theme='dark'] & {
    border-bottom: 2px solid #393939;
  }
`;

export const Footer: FC<FooterProps> = props => (
  <StyledFooterDiv>
    <StyledListWrapper>
      {props.content.map(column => (
        <StyledListColumn key={column.title}>
          <StyledListColumnTitle>{column.title}</StyledListColumnTitle>
          <StyledListColumnLinksList>
            {column.links.map(link => (
              <StyledListColumnLinksListItem key={link.title}>
                <StyledLink href={link.href}>
                  {link.title}
                  {link.highlightedText && (
                    <StyledHighlightedSpan>{link.highlightedText}</StyledHighlightedSpan>
                  )}
                </StyledLink>
              </StyledListColumnLinksListItem>
            ))}
          </StyledListColumnLinksList>
        </StyledListColumn>
      ))}
    </StyledListWrapper>
    <StyledBottomDiv>
      <StyledLineBreak />
      <StyledBottomLinksWrapper>
        <StyledSocialMediaLinksWrapper>
          <SocialMediaLinks links={props.socialMediaLinks} />
        </StyledSocialMediaLinksWrapper>
        <StyledBottomCenterDiv>
          {props.bottomLinks.map(link => (
            <StyledLink key={link.title} href={link.href} isBottom>
              {link.title}
            </StyledLink>
          ))}
        </StyledBottomCenterDiv>
        <StyledBottomRightDiv>{props.copyrightText}</StyledBottomRightDiv>
      </StyledBottomLinksWrapper>
    </StyledBottomDiv>
  </StyledFooterDiv>
);
